'use client';

import {clsx} from '@gfiber/typesmith';
import type React from 'react';
import {
  type FormEvent,
  type ForwardedRef,
  forwardRef,
  type HTMLInputAutoCompleteAttribute,
  type ReactNode,
  useEffect,
  useId,
  useState,
} from 'react';
import type {FieldValues, UseFormReturn} from 'react-hook-form';
import {FieldHints} from '../../FieldHints/FieldHints';
import styles from './OutlinedTextField.module.scss';

export interface OutlinedTextFieldProps<TFieldValues extends FieldValues> {
  className: string;
  invalid: boolean;
  placeholder: string;
  id: string;
  label: string;
  icon: React.JSX.Element;
  helpText?: ReactNode;
  errorText?: ReactNode;
  helpTextClass?: string;
  errorTextClass?: string;
  disabled: boolean;
  required: boolean;
  autoComplete: HTMLInputAutoCompleteAttribute | undefined;
  minLength: number;
  maxLength: number;
  value: string | number;
  secondaryBackground: boolean;
  tertiaryBackground: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: this is defined by react-hook-form
  onChange?: (...event: any[]) => void;
  // biome-ignore lint/suspicious/noExplicitAny: this is defined by react-hook-form
  onKeyDown?: (...event: any[]) => void;
  // biome-ignore lint/suspicious/noExplicitAny: this is defined by react-hook-form
  onFocus?: (...event: any[]) => void;
  // biome-ignore lint/suspicious/noExplicitAny: this is defined by react-hook-form
  onBlur?: (...event: any[]) => void;
  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'date';
  name?: string;
  dataTestId?: string;
  form?: UseFormReturn<TFieldValues>;
  autoFocus?: boolean;
  defaultValue?: string | number;
  max?: string | number;
  min?: string | number;
}

const OutlinedTextField = forwardRef(
  (
    props: Partial<OutlinedTextFieldProps<FieldValues>>,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const [inputValue, setInputValue] = useState(props.value);
    const baseId = useId();
    const helpTextId = `${baseId}-help`;
    const errorTextId = `${baseId}-error`;
    const ariaDescribedBy =
      [
        props.helpText ? helpTextId : '',
        props.invalid && props.errorText ? errorTextId : '',
      ]
        .filter(Boolean)
        .join(' ') || undefined;

    function updateInput(event: React.FormEvent<HTMLInputElement>) {
      const value = (event.target as HTMLInputElement).value;
      const form = props.form;
      const name = props.name;
      setInputValue(value);
      props.onChange?.(value);
      // Handle initial autofill to mark the field as dirty
      // in order to enable form submit button
      if (form && name && !form?.formState.isDirty) {
        form.setValue(name, value, {
          shouldValidate: true,
          shouldDirty: true,
        });
        form.trigger(name);
      }
    }

    useEffect(() => {
      setInputValue(props.value);
    }, [props.value]);

    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div
            className={clsx(
              props.className,
              styles.textfield,
              props.invalid && styles.hasError,
            )}
          >
            <input
              className={clsx(
                styles.textFieldInput,
                props.secondaryBackground && styles.secondaryBackground,
                props.tertiaryBackground && styles.tertiaryBackground,
              )}
              placeholder={props.placeholder || ''}
              disabled={props.disabled}
              minLength={props.minLength}
              maxLength={props.maxLength}
              defaultValue={props.defaultValue}
              value={inputValue}
              onInput={updateInput}
              onKeyDown={props.onKeyDown}
              onFocus={props.onFocus}
              onBlur={props.onBlur}
              data-testid={props.dataTestId || props.label}
              id={props.id || props.label}
              aria-describedby={ariaDescribedBy}
              type={props.type || 'text'}
              name={props.name || 'name'}
              required={props.required || false}
              autoComplete={props.autoComplete && props.autoComplete}
              ref={ref}
              // biome-ignore lint: used when necessary
              autoFocus={props.autoFocus}
              max={props.max}
              min={props.min}
            />
            <label
              htmlFor={props.id || props.label}
              className={clsx(
                styles.textFieldLabel,
                props.disabled && styles.disabled,
                props.secondaryBackground && styles.secondaryBackground,
                props.tertiaryBackground && styles.tertiaryBackground,
              )}
            >
              {props.label}
            </label>
          </div>
          <span className={styles.icon}>{props.icon}</span>
        </div>
        <FieldHints
          helpText={props.helpText}
          errorText={props.invalid ? props.errorText : ''}
          helpTextClass={props.helpTextClass}
          errorTextClass={props.errorTextClass}
          helpTextId={helpTextId}
          errorTextId={errorTextId}
          className={styles.fieldHints}
        />
      </div>
    );
  },
);

export {OutlinedTextField};
